<script>
    import "../project-card/projectCard.css";
    import { formatUnits } from "ethers/lib/utils";
    import { getProjectMetaService } from "../../../services/api";
    import {
        PROJECT_STATUSES,
        COMPLETE_TIME_FORMAT,
        RAISE_TYPES,
    } from "../../constants/constants";
    import { projectDetail } from "../../store";

    import {
        formateToPrecision,
        formatNumberComma,
        getAfterTimestampDate,
        getAfterTimeStampStrapi,
        getRelativeTime,
        getTime,
        getTokenPrice,
        timeString,
    } from "../../utils";
    import ProgressBar from "../progressbar/ProgressBar.svelte";
    import { createEventDispatcher, onDestroy } from "svelte";
    import NETWORKS from "../../constants/network";

    const dispatch = createEventDispatcher();

    export let project;
    let projectMeta,
        liveProjectTimeRemaining,
        raisePoint,
        tokenPrice,
        projectTier = "";

    let saleCardDetails = {
        raiseTarget: "",
        totalSupply: "",
        startTime: "",
        endTime: "",
        unitsAvailable: "",
        totalRaised: "",
        price: "",
    };

    let timer;
    $: if (
        projectMeta &&
        projectMeta.canEndStateConfig &&
        PROJECT_STATUSES.LIVE === project.project_status &&
        projectMeta.startEvent
    ) {
        setInterval(() => {
            const { timeRemaining } = getTime(projectMeta);
            liveProjectTimeRemaining = timeRemaining;
        }, 1000);
    }

    $: if (project) {
        if (
            project.project_status === PROJECT_STATUSES.UPCOMING &&
            !project.sale_address
        ) {
            raisePoint = tokenPrice = "TBC";
        } else {
            raisePoint = 
                saleCardDetails.raiseTarget ? 
                saleCardDetails.raiseTarget: 
                project.project_raise_target
            ;
            tokenPrice = `$${
                saleCardDetails.price ? formateToPrecision(saleCardDetails.price) ?? "" :
                formateToPrecision(project.project_token_price) ?? ""
            }`;
        }
    }

    $: if (!project.sale_address) {
        timer = setInterval(() => {
            const now = Math.floor(Date.now() / 1000);
            const end = new Date(project.project_end_time).getTime() / 1000;
            const timeRemaining =
                end - now > 0 ? timeString((end - now) * 1000) : "Raise Ended";
            liveProjectTimeRemaining = timeRemaining;
        }, 1000);
    }

    onDestroy(() => {
        clearTimeout(timer);
    });

    const setProjectData = async () => {
        if (project.sale_address && project.raise_type != "Old_Dutch_Auction" && project.version.data?.attributes?.version && project.chain.data?.attributes?.chain_id) {
            const { data } = await getProjectMetaService(
                project.sale_address,
                project.version.data.attributes.version,
                project.chain.data.attributes.chain_id
            );
            projectMeta = data && data.sales.length > 0 && data.sales[0];
            if (projectMeta && projectMeta.saleStatus > 1) {
                dispatch("completedProject", {
                    project,
                    timestamp: projectMeta.endEvent.timestamp,
                });
            }
            if (projectMeta) setSaleData(projectMeta);
        } else {
            saleCardDetails = {
                walletCap: project.individual_purchase_limit,
                raiseTarget: project.project_raise_target,
                totalSupply: project.total_available_token,
                startTime: new Date(project.project_start_time).toLocaleString(
                    "en-US"
                ),
                endTime: new Date(project.project_end_time).toLocaleString(
                    "en-US"
                ),
                totalRaised:
                    ((project.progress_bar_status *
                        project.total_available_token) /
                        100) *
                    project.project_token_price,
                price: project.project_token_price,
                tokenName: project.token_name,
                tokenSymbol: project.token_symbol,
                percentRaised: project.progress_bar_status,
            };
            const now = Math.floor(Date.now() / 1000);
            const end = new Date(saleCardDetails.endTime).getTime() / 1000;
            const timeRemaining =
                end - now > 0 ? timeString((end - now) * 1000) : "Raise Ended";
            liveProjectTimeRemaining = timeRemaining;
        }
    };
    const setSaleData = (sale) => {
        saleCardDetails = {
            raiseTarget: Number(
                formatUnits(sale.minimumRaise, sale.reserve.decimals)
            ),
            totalSupply: project.total_available_token,
            startTime: sale.canStartStateConfig,
            endTime: sale.endEvent ?? sale.canEndStateConfig,
            unitsAvailable: Number(
                +formatUnits(sale.unitsAvailable, sale.token.decimals)
            ),
            totalRaised: Number(
                +formatUnits(sale.totalRaised, sale.reserve.decimals)
            ),
            price: getTokenPrice(sale),
            tokenName: sale.token.name,
            tokenSymbol: sale.token.symbol,
            percentRaised: sale.percentRaised,
            saleStatus: sale.saleStatus,
        };
    };

    setProjectData();
    let flaggedIcon = "./images/flag-icon.svg";
    let featuredIcon = "./images/flag-icon.png";
    let verifiedIcon = "./images/verified.svg";

    $: projectNetwork = NETWORKS[project.chain?.data?.attributes.chain_id];
</script>

<div class="slider-wrapper flex">
    <a
        class="flex column-direction"
        on:click={() => {
            projectDetail.set(project);
        }}
        id={project.id}
        target={project.raise_type === "Old_Dutch_Auction" ? "_blank" : ""}
        href={project.raise_type === "Old_Dutch_Auction"
            ? `https://app.polinate.io/#/project/${
                  project.sale_address ? project.sale_address : ""
              }`
            : `#/project-details/${project.id}`}
    >
        <div class="card column-direction">
            <div class="card-body">
                {#if projectNetwork}
                    <span class="flex card-chain items-center">
                        <img src={projectNetwork.iconUrl} alt="iconUrl" />
                        {projectNetwork.shortName}
                    </span>
                {/if}
                <figure>
                    <img
                        src={project.project_cover?.data?.attributes?.formats?.thumbnail.url}
                        alt="cardImage"
                    />
                    <div class="card-redirect-icon-1">
                        <div>
                            {#if project.raise_type === "Old_Dutch_Auction"}
                                <div class="card-redirect-icon">
                                    <img
                                        src="./images/outside.svg"
                                        alt="outside"
                                    />
                                </div>
                            {/if}
                        </div>
                        <div class="flagged-image-wrap">
                            {#if project.flag}
                                <span class="flagged-image">
                                    <img
                                        src={flaggedIcon}
                                        alt="flagged image"
                                    />
                                    <small>
                                        <strong
                                            >This raise is void, DO NOT interact</strong
                                        >
                                    </small>
                                </span>
                            {/if}
                            {#if !project.featured && !project.is_verified}
                                <span class="flagged-image featured">
                                    <img
                                        src={featuredIcon}
                                        alt="featured image"
                                    />
                                    <small>
                                        <strong
                                            >Unvetted Raises are created through
                                            a Permissionless environment without
                                            any prior vetting and don’t
                                            represent an endorsement by Polinate</strong
                                        >
                                    </small>
                                </span>
                            {/if}
                        </div>
                    </div>
                </figure>
                <div class="tier-wrapper flex items-center">
                    <span
                        class={`status ${project.project_status.toLowerCase()}`}
                        >{project.project_status === PROJECT_STATUSES.LIVE
                            ? "Live"
                            : project.project_status}</span
                    >
                    <span class="status public ml-auto"
                        >{project.wallet_tier?.data?.attributes?.tier_label ?? "Public"}</span
                    >
                </div>
                <section class="text-left">
                    <h2>
                        {#if project.is_verified}
                            <span class="verified-wrapper">
                                <img
                                        class="verified"
                                        src={verifiedIcon}
                                        alt="featured image"
                                />
                                <small class="verified-hover">
                                    <strong>Verified</strong>
                                </small>
                            </span>
                        {/if}
                        {project.project_name}
                    </h2>
                    <p>{project.project_description}</p>
                    <ul class="list-none flex column-direction">
                        <li class="flex">
                            <span>Raise Target</span>
                            <span class="ml-auto">${isNaN(raisePoint)? raisePoint : formatNumberComma(Number(raisePoint).toFixed(2))}</span>
                        </li>
                        <li class="flex">
                            <span>Token Price</span>
                            <span class="ml-auto">{tokenPrice}</span>
                        </li>

                        <li class="flex">
                            <span>Raise Type</span>
                            <span class="ml-auto"
                                >{RAISE_TYPES[project.raise_type] ?? ""}</span
                            >
                        </li>

                        {#if PROJECT_STATUSES.LIVE === project.project_status}
                            <li class="flex">
                                <span>Time Remaining</span>
                                {#if project.sale_address && saleCardDetails.endTime}
                                    <span class="ml-auto"
                                        >{liveProjectTimeRemaining
                                            ? liveProjectTimeRemaining
                                            : getRelativeTime(
                                                  saleCardDetails.endTime
                                              )}</span
                                    >
                                {:else}
                                    <span class="ml-auto"
                                        >{liveProjectTimeRemaining}</span
                                    >
                                {/if}
                            </li>
                        {/if}
                        {#if PROJECT_STATUSES.UPCOMING === project.project_status}
                            <li class="flex">
                                <span>Start Time</span>
                                <span class="ml-auto"
                                    >{getAfterTimeStampStrapi(
                                        project.project_start_time,
                                        COMPLETE_TIME_FORMAT
                                    )}</span
                                >
                            </li>
                            <li class="flex">
                                <span>End Time</span>
                                <span class="ml-auto"
                                    >{getAfterTimeStampStrapi(
                                        project.project_end_time,
                                        COMPLETE_TIME_FORMAT
                                    )}</span
                                >
                            </li>
                        {/if}
                        {#if PROJECT_STATUSES.COMPLETED === project.project_status && saleCardDetails.startTime && saleCardDetails.endTime}
                            <li class="flex">
                                <span>Date Ending</span>
                                <span class="ml-auto"
                                    >{
                                    project.sale_address && project.raise_type != "Old_Dutch_Auction"
                                        ? getAfterTimestampDate(
                                              saleCardDetails.endTime,
                                              COMPLETE_TIME_FORMAT
                                          )
                                        : saleCardDetails.endTime}</span
                                >
                            </li>
                        {/if}
                        {#if PROJECT_STATUSES.UPCOMING !== project.project_status}
                            <li>
                                <ProgressBar {saleCardDetails} totalSupply={project.total_available_token} />
                            </li>
                        {/if}
                    </ul>
                </section>
            </div>
        </div>
    </a>
</div>
